// disabling as this is an exception case and a linting false positive
/* eslint-disable @typescript-eslint/no-invalid-void-type */
import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  retry
} from '@reduxjs/toolkit/query/react';
import { ConversionCountRequest } from 'common/types/conversionCountRequest';
import { CountDataRequest } from 'common/types/CountDataRequest';
import { RootState } from 'app/store';
import { REHYDRATE } from 'redux-persist';
import { PaginationResponse } from 'features/pagination/types/paginationResponse';
import { ConversionViewRequest } from 'features/conversions/forms/types/conversionSimpleViewRequest';
import { PaginationRequest } from 'features/pagination/types/paginationRequest';
import { cacher } from 'app/cacheUtils/cacheIndex';
import { getFileName } from 'common/helpers/headers-helper.';
import { downloadFile } from 'common/helpers/download-helper';
import { MappedRevenueExportRequest } from 'features/revenue/types/MappedRevenueExportRequest';
import {
  VisitorDetailRequest,
  VisitorDetailResponse
} from 'features/visitors/visitorObjects';
import { ConversionSimpleViewResponse } from 'features/conversions/forms/types/conversionSimpleViewResponse';
import { ConversionDetailedViewResponse } from 'features/conversions/forms/types/converisonDetailedViewResponse';
import { MMMSegmentsRequest } from 'features/marketing-mix-modelling/mmmSegmentsRequest';
import { MmmModelOutput } from 'features/marketing-mix-modelling/mmmModelOutput';
import {
  GetWebhooksResult,
  GetWebhookDataFieldsResult,
  GetWebhookHistoriesResult
} from 'features/webhooks/webhooks.models';
import {
  DataScienceTask,
  DataScienceTaskParametersDto,
  DataScienceTaskStagingDataDto,
  GetDataScienceTasksResult
} from 'features/data-science/tasks.models';
import { MmmConfigDto } from 'features/marketing-mix-modelling/budget-scenario-planner/models/mmm-config';
import {
  ConversionInboundCallExportRequest,
  ConversionInboundCallViewResponse
} from 'features/conversions/calls/conversion-inboundcalls.types';
import {
  GetRevenueResponse,
  RevenueByVisitorIdResponse
} from 'features/revenue/types/get-revenue-response';
import {
  GetRevenueRequest,
  RevenueByVisitorIdRequest
} from 'features/revenue/types/get-revenue-request';
import { OpportunityExportRequest } from 'features/opportunities/types/opportunities-export-request';
import {
  GetOpportunitiesResponse,
  OpportunitiesByVisitorIdResponse
} from 'features/opportunities/types/get-opportunities-response';
import {
  GetOpportunitiesRequest,
  OpportunitiesByVisitorIdRequest
} from 'features/opportunities/types/get-opportunities-request';
import { ConversionWebExportRequest } from 'features/conversions/forms/types/conversion-web-export-request';
import { Exclusion, ExclusionDto } from 'features/exclusions/exclusions.models';
import { CallRecordingResult } from 'features/conversions/calls/call-recording/call-recording-types';
import { ConversionPath } from 'features/conversions/paths/paths.models';
import { exclusionTypeLabelMap } from 'features/exclusions/exclusions.enums';
import { selectIsSandboxUser } from 'features/auth/authenticationSlice';
import { InsightQueryRequestDto } from 'features/insights/types/insights.types';
import { GetInsightResponse } from './advanced-reports/advanced-report-api.types';
import { AdvancedReportMetricRow } from 'features/advanced-reports/advanced-reports.types';
import { createInsightMetrics } from './advanced-reports/factories/advanced-report-metric-row-factory';
import { SegmentationConfig } from 'features/segmentation-config/segmentationConfig.model';
import { TabKey } from 'features/segmentation-config/segmentation-config-tab-keys';
import { ClientIntegrationDTO } from 'features/client-integrations/clientIntegration.model';
import {
  MmmModelChartOutputDto,
  MmmModelOutputDto
} from 'features/marketing-mix-modelling/budget-scenario-planner/models/mmm-model-output';

export interface SiteResponse {
  siteId: string;
  fullURL: string;
  suspended: boolean;
  APIKey: string;
  currency: string;
}

export interface UserSite {
  id: string;
  label: string;
  currency: string;
}

export interface CountResponse {
  [date: string]: number;
}

export interface AverageValueResponse {
  averageValue: number;
}

export interface VisitorRevenueTotalsResponse {
  percentageDifference: number;
  visitorRevenueTotal: number;
}

export interface MetricsRequest {
  siteId: string;
  startDate: string;
  endDate: string;
  conversionFilter: string;
}

const handleTransformResponse = (
  response: GetInsightResponse,
  _: unknown,
  request: InsightQueryRequestDto
): AdvancedReportMetricRow[] => {
  const metrics = response.metrics.flatMap((metric) =>
    createInsightMetrics(metric, request)
  );

  return metrics;
};

const removeWebTokensFromUrl = (url: string) =>
  url.replace('http://', '').replace('https://', '').replace('www.', '');

const prepareHeaders = (
  headers: Headers,
  { getState }: { getState: () => unknown }
) => {
  headers.set('Access-Control-Allow-Origin', '*');
  const token = (getState() as RootState).auth.accessToken;
  if (token != null) {
    headers.set('authorization', `Bearer ${token}`);
  }

  return headers;
};

const staggeredBaseQuery = retry(
  fetchBaseQuery({
    baseUrl: process.env.REACT_APP_LOCAL_API,
    prepareHeaders
  }),
  { maxRetries: 2 }
);

const customBaseQuery: BaseQueryFn = (...baseQueryArgs) => {
  const [args, { getState }] = baseQueryArgs;

  const isSandboxUser = selectIsSandboxUser(getState() as RootState);

  if (isSandboxUser) {
    return fetchBaseQuery({
      baseUrl: `${process.env.REACT_APP_LOCAL_API?.replace(
        /\/?$/,
        ''
      )}/sandbox`,
      prepareHeaders
    })(...baseQueryArgs);
  }

  if (args.url?.endsWith('/conversion/paths')) {
    return fetchBaseQuery({
      baseUrl: process.env.REACT_APP_LOCAL_API,
      prepareHeaders
    })(...baseQueryArgs);
  }

  return staggeredBaseQuery(...baseQueryArgs);
};

export const queryApi = createApi({
  keepUnusedDataFor: 3600,
  reducerPath: 'queryApi',
  baseQuery: customBaseQuery,
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE) {
      return action.payload?.[reducerPath];
    }
  },
  tagTypes: [
    ...cacher.defaultTags,
    'Sites',
    'Conversion-Metrics',
    'Revenue-Metrics',
    'Sales-Metrics',
    'Cost-Metrics',
    'Click-Metrics',
    'Visitor-Count',
    'Visitor-Detail',
    'Visitor-Detail-Revenue',
    'Conversion-Count',
    'Cost',
    'Revenue',
    'Revenue-Average',
    'Revenue-Average-LTV',
    'Opportunities',
    'Sales',
    'Conversion-Web',
    'Conversion-Call',
    'MMM-Segments',
    'MMM-Segment-Data',
    'Webhooks',
    'Exclusions',
    'WebPageViewSegmentationConfigs',
    'AdCostSegmentationConfigs',
    'WebPageViewSegmentationConfig',
    'AdCostSegmentationConfig',
    'DataScienceTasks',
    'DataScienceTaskParameters',
    'ClientIntegrations',
    'DataScienceTaskStagingData',
    'ClientIntegration',
    'MMM-Config',
    'MMM-Results-Data',
    'MMM-Chart-Results-Data',
    'Currencies-Config'
  ],
  endpoints: (builder) => ({
    refetchErroredQueries: builder.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ['UNKNOWN_ERROR']
    }),
    getSites: builder.query<UserSite[], void>({
      query: () => 'sites',
      providesTags: ['Sites'],
      transformResponse: (response: SiteResponse[]) => {
        return response.map((site) => ({
          id: site.siteId,
          label: removeWebTokensFromUrl(site.fullURL),
          currency: site.currency
        }));
      }
    }),
    getVisitorCount: builder.query<CountResponse, CountDataRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/visitor/count`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Visitor-Count', [
        'siteId',
        'startDate',
        'endDate'
      ])
    }),
    getMetrics: builder.query<CountResponse, MetricsRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/visitor/count`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            conversionFilter: request.conversionFilter
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Visitor-Count', [
        'siteId',
        'startDate',
        'endDate',
        'conversionFilter'
      ])
    }),
    getVisitorDetail: builder.query<
      VisitorDetailResponse,
      VisitorDetailRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/visitor/${request.visitorId}`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Visitor-Detail', [
        'siteId',
        'visitorId'
      ])
    }),
    getConversionCount: builder.query<CountResponse, ConversionCountRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/conversion/count`,
          params: {
            conversionType: request.conversionType,
            startDate: request.startDate,
            endDate: request.endDate
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Conversion-Count', [
        'siteId',
        'startDate',
        'endDate',
        'conversionFilter'
      ])
    }),
    getTotalAdCosts: builder.query<CountResponse, CountDataRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/adcost/totals`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Cost', [
        'siteId',
        'startDate',
        'endDate',
        'currencyCode'
      ])
    }),
    getTotalRevenue: builder.query<CountResponse, CountDataRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/totals`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Revenue', [
        'siteId',
        'startDate',
        'endDate'
      ])
    }),
    getTotalRevenueByVisitorId: builder.query<
      VisitorRevenueTotalsResponse,
      VisitorDetailRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/totals/${request.visitorId}`,
          params: {
            currencyCode: request.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Visitor-Detail-Revenue', [
        'siteId',
        'visitorId'
      ])
    }),
    getAverageRevenueBetweenDates: builder.query<number, CountDataRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/average`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Revenue-Average', [
        'siteId',
        'startDate',
        'endDate'
      ])
    }),
    getAverageVisitorLifetimeValue: builder.query<
      AverageValueResponse,
      CountDataRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/average-lifetime-value`,
          params: {
            startDateTimeIso: request.startDate,
            endDateTimeIso: request.endDate,
            currencyCode: request.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Revenue-Average-LTV', [
        'siteId',
        'startDate',
        'endDate'
      ])
    }),
    getCountRevenue: builder.query<CountResponse, CountDataRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/count`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate
          }
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('Sales', [
        'siteId',
        'startDate',
        'endDate'
      ])
    }),
    getConversionsExport: builder.mutation<Blob, ConversionWebExportRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/conversions/export`,
          params: {
            viewType: request.viewType,
            conversionType: request.conversionType,
            startDate: request.startDate,
            endDate: request.endDate,
            search: request.searchTerm
          },
          cache: 'no-cache',
          responseHandler: async (response: {
            blob: () => any;
            headers: Headers;
          }) => {
            const blob = await response.blob();
            const fileName = getFileName(response.headers);

            downloadFile(blob, fileName);
          }
        };
      }
    }),
    getConversionInboundCallExport: builder.mutation<
      Blob,
      ConversionInboundCallExportRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/conversion/inboundcalls/export`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            search: request.searchTerm
          },
          cache: 'no-cache',
          responseHandler: async (response: {
            blob: () => any;
            headers: Headers;
          }) => {
            const blob = await response.blob();
            const fileName = getFileName(response.headers);

            downloadFile(blob, fileName);
          }
        };
      }
    }),
    getConversionInboundCallRecordingUrl: builder.query<
      CallRecordingResult,
      { siteId: string; eventId: string }
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/conversion/inboundcalls/${request.eventId}/recording`,
          cache: 'no-cache'
        };
      }
    }),
    getRevenueExport: builder.mutation<Blob, MappedRevenueExportRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/export`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode,
            search: request.searchTerm
          },
          cache: 'no-cache',
          responseHandler: async (response: {
            blob: () => any;
            headers: Headers;
          }) => {
            const blob = await response.blob();
            const fileName = getFileName(response.headers);

            downloadFile(blob, fileName);
          }
        };
      }
    }),
    getOpportunitiesExport: builder.mutation<Blob, OpportunityExportRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/opportunities/export`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode,
            search: request.searchTerm
          },
          cache: 'no-cache',
          responseHandler: async (response: {
            blob: () => any;
            headers: Headers;
          }) => {
            const blob = await response.blob();
            const fileName = getFileName(response.headers);

            downloadFile(blob, fileName);
          }
        };
      }
    }),
    getPaginatedConversions: builder.query<
      PaginationResponse<
        ConversionSimpleViewResponse | ConversionDetailedViewResponse
      >,
      PaginationRequest<ConversionViewRequest>
    >({
      query: (paginationRequest) => {
        return {
          url: `${paginationRequest.requestDetails.siteId}/conversions`,
          params: {
            conversionType: paginationRequest.requestDetails.conversionType,
            viewType: paginationRequest.requestDetails.viewType,
            startDate: paginationRequest.startDate,
            endDate: paginationRequest.endDate,
            page: paginationRequest.page,
            pageSize: paginationRequest.pageSize,
            search: paginationRequest.searchTerm,
            seekId: paginationRequest?.seekId,
            seekValue: paginationRequest?.seekValue,
            seekColumnName: paginationRequest?.seekColumnName,
            seekDirection: paginationRequest?.seekDirection,
            orderDirection: paginationRequest?.orderDirection
          }
        };
      },
      providesTags: cacher.cacheByPaginationQueryArgs('Conversion-Web'),
      transformResponse: (
        baseQueryReturnValue: unknown,
        meta: undefined,
        arg: PaginationRequest<ConversionViewRequest>
      ) => {
        const typedResponse =
          arg.requestDetails.viewType === 'simple'
            ? (baseQueryReturnValue as PaginationResponse<ConversionSimpleViewResponse>)
            : (baseQueryReturnValue as PaginationResponse<ConversionDetailedViewResponse>);

        if (arg.seekDirection === 'pageBackward') {
          typedResponse.data = typedResponse.data.reverse();
        }

        return typedResponse;
      }
    }),
    getPaginatedRevenue: builder.query<
      PaginationResponse<GetRevenueResponse>,
      PaginationRequest<GetRevenueRequest>
    >({
      query: (paginationRequest) => {
        return {
          url: `${paginationRequest.requestDetails.siteId}/revenue`,
          params: {
            startDate: paginationRequest.startDate,
            endDate: paginationRequest.endDate,
            page: paginationRequest.page,
            pageSize: paginationRequest.pageSize,
            search: paginationRequest.searchTerm,
            seekId: paginationRequest?.seekId,
            seekValue: paginationRequest?.seekValue,
            seekColumnName: paginationRequest?.seekColumnName,
            seekDirection: paginationRequest?.seekDirection,
            orderDirection: paginationRequest?.orderDirection,
            currencyCode: paginationRequest.requestDetails.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByPaginationQueryArgs('Revenue'),
      transformResponse: (
        baseQueryReturnValue: unknown,
        meta: undefined,
        arg: PaginationRequest<GetRevenueRequest>
      ) => {
        const typedResponse =
          baseQueryReturnValue as PaginationResponse<GetRevenueResponse>;

        if (arg.seekDirection === 'pageBackward') {
          typedResponse.data = typedResponse.data.reverse();
        }

        return typedResponse;
      }
    }),
    getPaginatedOpportunities: builder.query<
      PaginationResponse<GetOpportunitiesResponse>,
      PaginationRequest<GetOpportunitiesRequest>
    >({
      query: (paginationRequest) => {
        return {
          url: `${paginationRequest.requestDetails.siteId}/opportunities`,
          params: {
            startDate: paginationRequest.startDate,
            endDate: paginationRequest.endDate,
            page: paginationRequest.page,
            pageSize: paginationRequest.pageSize,
            search: paginationRequest.searchTerm,
            seekId: paginationRequest?.seekId,
            seekValue: paginationRequest?.seekValue,
            seekColumnName: paginationRequest?.seekColumnName,
            seekDirection: paginationRequest?.seekDirection,
            orderDirection: paginationRequest?.orderDirection,
            currencyCode: paginationRequest.requestDetails.currencyCode
          }
        };
      },
      providesTags: cacher.cacheByPaginationQueryArgs('Opportunities'),
      transformResponse: (
        baseQueryReturnValue: unknown,
        meta: undefined,
        arg: PaginationRequest<GetOpportunitiesRequest>
      ) => {
        const typedResponse =
          baseQueryReturnValue as PaginationResponse<GetOpportunitiesResponse>;

        if (arg.seekDirection === 'pageBackward') {
          typedResponse.data = typedResponse.data.reverse();
        }

        return typedResponse;
      }
    }),
    getPaginatedConversionInboundCalls: builder.query<
      PaginationResponse<ConversionInboundCallViewResponse>,
      PaginationRequest<ConversionViewRequest>
    >({
      query: (paginationRequest) => {
        return {
          url: `${paginationRequest.requestDetails.siteId}/conversion/inboundcalls`,
          params: {
            startDate: paginationRequest.startDate,
            endDate: paginationRequest.endDate,
            pageSize: paginationRequest.pageSize,
            page: paginationRequest.page,
            search: paginationRequest.searchTerm,
            seekId: paginationRequest?.seekId,
            seekValue: paginationRequest?.seekValue,
            seekColumnName: paginationRequest?.seekColumnName,
            seekDirection: paginationRequest?.seekDirection,
            orderDirection: paginationRequest?.orderDirection
          }
        };
      },
      providesTags: cacher.cacheByPaginationQueryArgs('Conversion-Call'),
      transformResponse: (baseQueryReturnValue, meta, arg) => {
        const response =
          baseQueryReturnValue as PaginationResponse<ConversionInboundCallViewResponse>;

        const orderedData =
          arg.seekDirection === 'pageBackward'
            ? response.data.reverse()
            : response.data;

        const finalResponse: PaginationResponse<ConversionInboundCallViewResponse> =
          { ...response, data: orderedData };

        return finalResponse;
      }
    }),
    getDataScienceMmmSegments: builder.query<string[], MMMSegmentsRequest>({
      query: (request) => {
        return {
          url: `${request.siteId}/data-science/mmm/segments`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('MMM-Segments', ['siteId'])
    }),
    getDataScienceMmmSegmentData: builder.query<
      MmmModelOutput,
      { siteId: string; segment: string }
    >({
      query: ({ siteId, segment }) => {
        return {
          url: `${siteId}/data-science/mmm/segments/${segment}`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('MMM-Segment-Data', [
        'siteId',
        'segment'
      ]),
      transformResponse: (
        baseQueryReturnValue: unknown,
        meta: undefined,
        arg: { siteId: string; segment: string }
      ) => {
        const typedResponse = baseQueryReturnValue as MmmModelOutput;

        const baseOffsets: { [key: string]: number } = {
          '644b77c160b59': 0.6,
          '644b9d48c71da': 0.6,
          '641d914945eb2': 0.85,
          demo: 1
        };

        typedResponse.dayMultiplier =
          typedResponse.dayMultiplier * baseOffsets[arg.siteId];

        return typedResponse;
      }
    }),
    getDataScienceTaskDefaultParameters: builder.query<
      DataScienceTaskParametersDto,
      { siteId: string; dataScienceFeature: string }
    >({
      query: ({ siteId, dataScienceFeature }) => {
        return {
          url: `${siteId}/data-science/tasks/${dataScienceFeature}/parameters`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties(
        'DataScienceTaskParameters',
        ['siteId', 'dataScienceFeature']
      ),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse =
          baseQueryReturnValue as DataScienceTaskParametersDto;
        return typedResponse;
      }
    }),
    getDataScienceTaskStagingData: builder.query<
      DataScienceTaskStagingDataDto[],
      { siteId: string; dataScienceFeature: string }
    >({
      query: ({ siteId, dataScienceFeature }) => {
        return {
          url: `${siteId}/data-science/tasks/${dataScienceFeature}/staging`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties(
        'DataScienceTaskStagingData',
        ['siteId', 'dataScienceFeature']
      ),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse =
          baseQueryReturnValue as DataScienceTaskStagingDataDto[];
        return typedResponse;
      }
    }),
    getDataScienceMmmConfig: builder.query<MmmConfigDto[], { siteId: string }>({
      query: ({ siteId }) => {
        return {
          url: `${siteId}/data-science/model-config/mmm`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('MMM-Config', ['siteId']),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse = baseQueryReturnValue as MmmConfigDto[];
        return typedResponse;
      }
    }),
    getDataScienceMmmOutputResults: builder.query<
      MmmModelOutputDto[],
      {
        siteId: string;
        modelRunId: string;
        modelRunDate: string;
        segment: string;
        modelType: string;
        timeRangeType: string;
      }
    >({
      query: ({
        siteId,
        modelRunId,
        modelRunDate,
        segment,
        modelType,
        timeRangeType
      }) => {
        return {
          url: `${siteId}/data-science/model-results/mmm/model-runs/${modelRunId}/model-run-dates/${modelRunDate}/segments/${segment}/model-type/${modelType}/time-range-type/${timeRangeType}`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('MMM-Results-Data', [
        'siteId',
        'modelRunId',
        'modelRunDate',
        'segment',
        'modelType',
        'timeRangeType'
      ]),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse = baseQueryReturnValue as MmmModelOutputDto[];
        return typedResponse;
      }
    }),
    getWebhooks: builder.query<GetWebhooksResult, { siteId: string }>({
      query: ({ siteId }) => `${siteId}/webhooks`,
      providesTags: cacher.cacheByQueryArgProperties('Webhooks', ['siteId'])
    }),
    getWebhookDataFields: builder.query<
      GetWebhookDataFieldsResult,
      { siteId: string; webhookType: string }
    >({
      query: ({ siteId, webhookType }) =>
        `${siteId}/webhooks/${webhookType}/datafields`
    }),
    getWebhookHistories: builder.query<
      GetWebhookHistoriesResult,
      { siteId: string; webhookId: string }
    >({
      query: ({ siteId, webhookId }) =>
        `${siteId}/webhooks/${webhookId}/history`
    }),
    getDataScienceTasks: builder.query<DataScienceTask[], { siteId: string }>({
      query: ({ siteId }) => `${siteId}/data-science/tasks`,
      providesTags: cacher.cacheByQueryArgProperties('DataScienceTasks', [
        'siteId'
      ]),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse = baseQueryReturnValue as GetDataScienceTasksResult;
        return typedResponse.dataScienceTasks;
      }
    }),
    getConversionPaths: builder.query<
      ConversionPath[],
      {
        siteId: string;
        startDate: string;
        endDate: string;
        currencyCode: string;
      }
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/conversion/paths`,
          params: {
            startDate: request.startDate,
            endDate: request.endDate,
            currencyCode: request.currencyCode
          }
        };
      }
    }),
    getExclusions: builder.query<Exclusion[], { siteId: string }>({
      query: ({ siteId }) => `${siteId}/exclusions`,
      providesTags: cacher.cacheByQueryArgProperties('Exclusions', ['siteId']),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse = baseQueryReturnValue as ExclusionDto[];
        return typedResponse.map((exclusion) => ({
          ...exclusion,
          exclusionType: exclusionTypeLabelMap[exclusion.exclusionType]
        }));
      }
    }),
    getRevenueByVisitorId: builder.query<
      RevenueByVisitorIdResponse[],
      RevenueByVisitorIdRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/revenue/${request.visitorId}`,
          params: {
            currencyCode: request.currency
          }
        };
      }
    }),
    getOpportunitiesByVisitorId: builder.query<
      OpportunitiesByVisitorIdResponse[],
      OpportunitiesByVisitorIdRequest
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/opportunities/${request.visitorId}`,
          params: {
            currencyCode: request.currency
          }
        };
      }
    }),
    getInboundCallRecordingUrlByHash: builder.query<
      CallRecordingResult,
      { siteId: string; hash: string }
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/inboundcalls/recording/${request.hash}`,
          cache: 'no-cache'
        };
      }
    }),
    getConversionInfluenceInsights: builder.mutation<
      AdvancedReportMetricRow[],
      InsightQueryRequestDto
    >({
      query: (request) => {
        return {
          url: `${request.siteId}/insights`,
          method: 'POST',
          body: request
        };
      },
      transformResponse: handleTransformResponse
    }),
    getWebPageViewSegmentationConfigs: builder.query<
      SegmentationConfig[],
      { siteId: string }
    >({
      query: (request) => ({
        url: `${request.siteId}/web-touchpoint/segmentation/config/`
      }),
      providesTags: cacher.cacheByQueryArgProperties(
        'WebPageViewSegmentationConfigs',
        ['siteId']
      )
    }),
    getAdCostSegmentationConfigs: builder.query<
      SegmentationConfig[],
      { siteId: string }
    >({
      query: (request) => ({
        url: `${request.siteId}/adcost/segmentation/config/`
      }),
      providesTags: cacher.cacheByQueryArgProperties(
        'AdCostSegmentationConfigs',
        ['siteId']
      )
    }),
    getSegmentationConfig: builder.query<
      SegmentationConfig,
      { siteId: string; configId: string; category: TabKey | null }
    >({
      query: ({ siteId, configId, category }) => {
        const isWebPageView = category === TabKey.Forms;
        const domain = isWebPageView ? `web-touchpoint` : `adcost`;

        return { url: `${siteId}/${domain}/segmentation/config/${configId}` };
      },
      providesTags: (result, error, { siteId, configId, category }) => {
        const tag =
          category === TabKey.Forms
            ? 'WebPageViewSegmentationConfig'
            : 'AdCostSegmentationConfig';

        return [{ type: tag, id: configId, siteId }];
      }
    }),
    getClientIntegrations: builder.query<
      ClientIntegrationDTO[],
      { siteId: string }
    >({
      query: (request) => ({
        url: `${request.siteId}/client-integrations/`
      }),
      providesTags: cacher.cacheByQueryArgProperties('ClientIntegrations', [
        'siteId'
      ])
    }),
    getClientIntegration: builder.query<
      ClientIntegrationDTO,
      {
        siteId: string;
        platformCategory: string;
        platform: string;
        clientIntegrationType: string;
      }
    >({
      query: (request) => ({
        url: `${request.siteId}/client-integrations/${request.platformCategory}/${request.platform}/${request.clientIntegrationType}`
      }),
      providesTags: cacher.cacheByQueryArgProperties('ClientIntegration', [
        'siteId',
        'platformCategory',
        'platform',
        'clientIntegrationType'
      ])
    }),
    getDataScienceMmmChartResults: builder.query<
      MmmModelChartOutputDto,
      {
        siteId: string;
        modelRunId: string;
        modelRunDate: string;
        segment: string;
        segmentValue: string;
        modelType: string;
        timeRangeType: string;
      }
    >({
      query: ({
        siteId,
        modelRunId,
        modelRunDate,
        segment,
        segmentValue,
        modelType,
        timeRangeType
      }) => {
        return {
          url: `${siteId}/data-science/model-results/mmm/chart-results/model-runs/${modelRunId}/model-run-dates/${modelRunDate}/segments/${segment}/segmentsValue/${segmentValue}/model-type/${modelType}/time-range-type/${timeRangeType}`
        };
      },
      providesTags: cacher.cacheByQueryArgProperties('MMM-Chart-Results-Data', [
        'siteId',
        'modelRunId',
        'modelRunDate',
        'segment',
        'segmentValue',
        'modelType',
        'timeRangeType'
      ]),
      transformResponse: (baseQueryReturnValue: unknown) => {
        const typedResponse = baseQueryReturnValue as MmmModelChartOutputDto;
        return typedResponse;
      }
    }),
    getSiteCurrencies: builder.query<string[], { siteId: string }>({
      query: ({ siteId }) => ({
        url: `${siteId}/currencies/config`
      }),
      providesTags: cacher.cacheByQueryArgProperties('Currencies-Config', [
        'siteId'
      ])
    })
  })
});

export const {
  useGetSitesQuery,
  useGetVisitorCountQuery,
  useGetVisitorDetailQuery,
  useGetTotalRevenueByVisitorIdQuery,
  useGetConversionCountQuery,
  useGetTotalAdCostsQuery,
  useGetTotalRevenueQuery,
  useGetCountRevenueQuery,
  useGetAverageRevenueBetweenDatesQuery,
  useGetAverageVisitorLifetimeValueQuery,
  useGetMetricsQuery,
  useGetConversionsExportMutation,
  useGetConversionInboundCallExportMutation,
  useGetRevenueExportMutation,
  useGetOpportunitiesExportMutation,
  useGetPaginatedConversionsQuery,
  useGetPaginatedRevenueQuery,
  useGetPaginatedOpportunitiesQuery,
  useGetPaginatedConversionInboundCallsQuery,
  useGetConversionInboundCallRecordingUrlQuery,
  useGetDataScienceMmmSegmentsQuery,
  useGetDataScienceMmmSegmentDataQuery,
  useGetWebhooksQuery,
  useGetWebhookDataFieldsQuery,
  useGetWebhookHistoriesQuery,
  useGetExclusionsQuery,
  useGetDataScienceTasksQuery,
  useGetConversionPathsQuery,
  usePrefetch,
  useGetOpportunitiesByVisitorIdQuery,
  useGetRevenueByVisitorIdQuery,
  useGetInboundCallRecordingUrlByHashQuery,
  useGetConversionInfluenceInsightsMutation,
  useGetWebPageViewSegmentationConfigsQuery,
  useGetSegmentationConfigQuery,
  useGetAdCostSegmentationConfigsQuery,
  useGetDataScienceTaskDefaultParametersQuery,
  useGetClientIntegrationsQuery,
  useGetDataScienceTaskStagingDataQuery,
  useGetClientIntegrationQuery,
  useGetDataScienceMmmConfigQuery,
  useGetDataScienceMmmOutputResultsQuery,
  useGetDataScienceMmmChartResultsQuery,
  useGetSiteCurrenciesQuery
} = queryApi;
